import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Box, IconButton, Sheet, Stack, Typography } from "@mui/joy";
import CustomSwitch from "components/common/CustomSwitch";
import { theme } from "styles/theme";
import {
  buildTaskEditorBlockGroups,
  getTaskEditorBlockDisplayLabels,
  TaskEditorState,
} from "./utils/taskEditorUtils";
import PreviewBlock from "./preview/PreviewBlock";
import PreviewGoNextButton from "./preview/PreviewGoNextButton";
import { PreviewFooterBlocks } from "./preview/footerBlocks";
import usePreviewSession from "./preview/usePreviewSession";
import { taskEditorScrollbarSx } from "./utils/taskEditorScrollbarSx";

// 정적 모드 분기 미리보기 — 옵션을 하나씩 ‹ › 로 넘겨 본다. 편집 뷰의
// 플로우차트 커넥터(선)는 미리보기에선 생략하고(환자 화면에 가깝게),
// 숨은 옵션이 레이아웃 높이·옆 여백에 비치지 않도록 현재 옵션만 렌더한다.
function PreviewBranchPager({
  groupIndex,
  optionCount,
  getOptionLabel,
  renderOption,
}: {
  groupIndex: number;
  optionCount: number;
  getOptionLabel: (optionIndex: number) => string;
  renderOption: (optionIndex: number) => React.ReactNode;
}) {
  const [optionIndex, setOptionIndex] = useState(0);
  // 옵션 삭제로 범위가 줄어든 경우에도 안전하도록 렌더 시점에 clamp.
  const current = Math.min(optionIndex, optionCount - 1);

  return (
    <Stack spacing={1}>
      {/* 분기 시작 행 — 끝 표시(점선 구분선)와 짝을 이루는 점선을 양옆에 깐다. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ minWidth: 0, px: "4px" }}
      >
        <Box
          sx={{
            flex: 1,
            borderTop: "1px dashed",
            borderColor: theme.vars.palette.Border.Weak,
          }}
        />
        {optionCount > 1 && (
          <IconButton
            size="sm"
            variant="plain"
            aria-label="이전 옵션"
            disabled={current === 0}
            data-testid={`task-preview-branch-prev-${groupIndex}`}
            onClick={() => setOptionIndex(Math.max(0, current - 1))}
            sx={{ minWidth: "28px", minHeight: "28px", borderRadius: "999px" }}
          >
            <span
              style={{ fontSize: "18px" }}
              className="material-symbols-rounded"
            >
              chevron_left
            </span>
          </IconButton>
        )}
        <Typography
          noWrap
          sx={{
            minWidth: 0,
            fontSize: "12px",
            fontWeight: 500,
            color: theme.vars.palette.Content.Medium,
          }}
        >
          {getOptionLabel(current)}
        </Typography>
        {optionCount > 1 && (
          <IconButton
            size="sm"
            variant="plain"
            aria-label="다음 옵션"
            disabled={current >= optionCount - 1}
            data-testid={`task-preview-branch-next-${groupIndex}`}
            onClick={() =>
              setOptionIndex(Math.min(optionCount - 1, current + 1))
            }
            sx={{ minWidth: "28px", minHeight: "28px", borderRadius: "999px" }}
          >
            <span
              style={{ fontSize: "18px" }}
              className="material-symbols-rounded"
            >
              chevron_right
            </span>
          </IconButton>
        )}
        <Box
          sx={{
            flex: 1,
            borderTop: "1px dashed",
            borderColor: theme.vars.palette.Border.Weak,
          }}
        />
      </Stack>
      <Box data-testid={`task-preview-branch-column-${groupIndex}-${current}`}>
        {renderOption(current)}
      </Box>
      {/* 분기 구간이 끝나는 지점 표시 — 아래부터는 어떤 옵션을 골라도
          공통으로 이어지는 블록. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: "4px" }}
        data-testid={`task-preview-branch-end-${groupIndex}`}
      >
        <Box
          sx={{
            flex: 1,
            borderTop: "1px dashed",
            borderColor: theme.vars.palette.Border.Weak,
          }}
        />
        <Typography
          sx={{
            flexShrink: 0,
            fontSize: "12px",
            fontWeight: 500,
            color: theme.vars.palette.Content.Medium,
          }}
        >
          분기 끝
        </Typography>
        <Box
          sx={{
            flex: 1,
            borderTop: "1px dashed",
            borderColor: theme.vars.palette.Border.Weak,
          }}
        />
      </Stack>
    </Stack>
  );
}

interface TaskEditorLivePreviewProps {
  task?: TaskEditorState;
  // 블록의 ✎ 버튼으로 편집 탭의 해당 블록(0-based index)으로 이동시키는 콜백.
  // 정적/체험 모드 공통 — 블록 본문은 클릭 타깃이 아니라 체험 인터랙션과 충돌 없음.
  // index 는 이 미리보기가 받은 task.blocks 기준 raw — 스코프/드릴다운 매핑은
  // 부모(패널)가 한다.
  onEditBlock?: (blockIndex: number) => void;
  // 미리보기 탭이 실제로 보이는지. 패널이 탭 전환 시 컴포넌트를 display:none 으로
  // 숨긴 채 마운트 유지하므로, 숨겨진 동안 window Enter 리스너가 편집 탭의 키 입력을
  // 가로채지 않게 게이트한다.
  isActive?: boolean;
  // false 면 푸터(마치기) 블록 묶음을 그리지 않는다 — 분기 내(세그먼트 일부)
  // 미리보기처럼 활동지 전체가 아닐 때.
  showFooterBlocks?: boolean;
}

const TASK_EDITOR_PREVIEW_SYNC_DELAY_MS = 300;

type ViewerMode = "patient" | "coach";

// 빈 상태 (task 없을 때)
function EmptyState() {
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: "12px",
        border: "1px dashed",
        borderColor: theme.vars.palette.Border.Weak,
        backgroundColor: theme.vars.palette.Background.Level1,
        textAlign: "center",
      }}
    >
      <Typography
        variant="Footnote"
        sx={{
          color: theme.vars.palette.Content.Weak,
          fontSize: "12px",
        }}
      >
        활동지를 선택하면 미리보기가 표시됩니다.
      </Typography>
    </Box>
  );
}

// 체험 모드 + Refresh 컨트롤 (patient 탭에서만 노출)
function TrialControls({
  isTrialEnabled,
  onToggleTrial,
  onRefresh,
}: {
  isTrialEnabled: boolean;
  onToggleTrial: (enabled: boolean) => void;
  onRefresh: () => void;
}) {
  return (
    <Stack
      direction="row"
      spacing="6px"
      sx={{ alignItems: "center", flexShrink: 0 }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 500,
          color: theme.vars.palette.Content.Weak,
        }}
      >
        체험 모드
      </Typography>
      <CustomSwitch
        checked={isTrialEnabled}
        onChange={(event) => onToggleTrial(event.target.checked)}
        slotProps={{ input: { "aria-label": "체험 모드" } }}
      />
      {/* refresh 버튼 자리는 체험 모드 OFF 일 때도 reserve 해 Switch 가 좌우로
          흔들리지 않도록 함 — visibility 만 토글. ON 값은 "visible" 이 아닌
          "inherit" — explicit visible 은 탭 오버레이(조상)의 visibility:hidden 을
          override 해 편집/코드 탭 위에 잔상으로 남는다. */}
      <Box
        sx={{
          width: "1px",
          height: "12px",
          backgroundColor: theme.vars.palette.Border.Weak,
          mx: "2px",
          visibility: isTrialEnabled ? "inherit" : "hidden",
        }}
      />
      <IconButton
        variant="plain"
        color="neutral"
        size="sm"
        aria-label="프리뷰 새로고침"
        onClick={onRefresh}
        sx={{
          minWidth: "26px",
          minHeight: "26px",
          borderRadius: "6px",
          color: theme.vars.palette.Content.Weak,
          visibility: isTrialEnabled ? "inherit" : "hidden",
          "&:hover": {
            color: theme.vars.palette.Content.Highlight,
          },
        }}
      >
        <RefreshRoundedIcon sx={{ fontSize: "16px" }} />
      </IconButton>
    </Stack>
  );
}

// 헤더 — 체험 모드 토글 + 리프레시. 내담자/코치 세그먼트는 제거됨.
function PreviewHeader({
  isTrialEnabled,
  onToggleTrial,
  onRefresh,
}: {
  isTrialEnabled: boolean;
  onToggleTrial: (enabled: boolean) => void;
  onRefresh: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        flexShrink: 0,
      }}
    >
      <TrialControls
        isTrialEnabled={isTrialEnabled}
        onToggleTrial={onToggleTrial}
        onRefresh={onRefresh}
      />
    </Box>
  );
}

function TaskEditorLivePreview({
  task,
  onEditBlock,
  isActive = true,
  showFooterBlocks = true,
}: TaskEditorLivePreviewProps) {
  // 내담자/코치 탭이 제거되어 항상 내담자 시점으로 고정.
  const viewerMode: ViewerMode = "patient";
  const [isTrialEnabled, setIsTrialEnabled] = useState(false);
  const [previewTask, setPreviewTask] = useState(task);

  // 300ms 디바운스 sync
  useEffect(() => {
    if (!task) return;
    const t = window.setTimeout(
      () => setPreviewTask(task),
      TASK_EDITOR_PREVIEW_SYNC_DELAY_MS
    );
    return () => window.clearTimeout(t);
  }, [task]);

  const session = usePreviewSession(previewTask, {
    viewerMode,
    isTrialEnabled,
  });

  // 분기 그룹 구조 — 정적 모드의 옵션별 레일 그룹 렌더에 쓰인다.
  const blockGroups = useMemo(
    () => buildTaskEditorBlockGroups(previewTask?.blocks ?? []),
    [previewTask]
  );

  const displayLabels = useMemo(
    () => getTaskEditorBlockDisplayLabels(previewTask?.blocks ?? []),
    [previewTask]
  );

  const isInteractive = viewerMode === "patient" && isTrialEnabled;
  const blockRefs = useRef<Map<number, HTMLElement>>(new Map());
  const prevRevealedUpToRef = useRef(0);
  // v1 TaskContent.tsx 와 동일하게 컨테이너 높이의 절반을 paddingBottom 으로
  // 두어야 마지막 블록도 scrollIntoView({ block:"center" }) 가 중앙 정렬 가능.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const update = () => setContainerHeight(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // v1 호환: Enter 로 다음 블록 이동. v1 처럼 textarea 안에서도 Enter 는 진행시키고,
  // 줄바꿈은 Shift+Enter 로 한다. 단 한글 등 IME 조합 확정용 Enter 는 진행 제외.
  useEffect(() => {
    if (!isInteractive || !isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;
      if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
      // IME 조합 확정 Enter (한글 등) 는 블록 진행으로 보지 않는다.
      if (
        e.isComposing ||
        (e as unknown as { keyCode?: number }).keyCode === 229
      )
        return;
      const upTo = session.state.revealedUpTo;
      const totalBlocks = previewTask?.blocks.length ?? 0;
      if (upTo <= 0 || upTo >= totalBlocks) return;
      const lastIdx = upTo - 1;
      const block = previewTask?.blocks[lastIdx];
      // 분기 블록은 옵션 클릭으로만 진행
      if (block?.action?.type === "button_branch") return;
      if (!session.isBlockComplete(lastIdx)) return;
      e.preventDefault();
      session.advanceFrom(lastIdx);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isInteractive, isActive, session, previewTask]);

  // 새로 reveal 된 블록으로 부드럽게 스크롤. 초기 reveal 이거나 reset 으로
  // upTo 가 줄어든 경우엔 스크롤하지 않는다.
  useEffect(() => {
    const upTo = session.state.revealedUpTo;
    const prev = prevRevealedUpToRef.current;
    prevRevealedUpToRef.current = upTo;
    if (!isInteractive) return;
    if (prev === 0 || upTo <= prev) return;
    const node = blockRefs.current.get(upTo);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [session.state.revealedUpTo, isInteractive]);

  if (!task) {
    return <EmptyState />;
  }

  const mode: "static" | "interactive" = isInteractive
    ? "interactive"
    : "static";

  // 체험 모드를 처음 상태로 되돌린다. revealedUpTo 와 입력값을 모두 비우고,
  // 사용자가 다시 첫 블록부터 진행할 수 있게 한다. 체험 모드 OFF 일 때는
  // 시각상 모든 블록이 그대로 보여 변화가 없는 게 정상.
  const handleRefresh = () => {
    session.reset();
  };

  const blocks = previewTask?.blocks ?? [];

  // 푸터(마치기) 블록은 참고용 정적 표시 — blocks JSON 과 무관. 정적 모드에서는
  // 항상, 체험(interactive) 모드에서는 모든 블록을 끝까지 진행했을 때만 하단에
  // 노출한다 (v1 흐름과 동일). 마지막 노출 블록이 미응답 분기면 (분기가 활동지
  // 끝이라 revealedUpTo 가 이미 끝에 닿은 경우) 선택 전까지 푸터를 보류한다.
  const lastIncludedIdx = (() => {
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      if (!session.excludedSequences.has(i + 1)) return i;
    }
    return -1;
  })();
  const lastIncludedBlock =
    lastIncludedIdx >= 0 ? blocks[lastIncludedIdx] : undefined;
  const lastBranchUnanswered =
    lastIncludedBlock?.action?.type === "button_branch" &&
    (session.state.buttonSelections[lastIncludedIdx] ?? []).length === 0;
  const showFooter =
    showFooterBlocks &&
    (mode === "static" ||
      (session.state.revealedUpTo >= blocks.length && !lastBranchUnanswered));

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
      <PreviewHeader
        isTrialEnabled={isTrialEnabled}
        onToggleTrial={setIsTrialEnabled}
        onRefresh={handleRefresh}
      />
      <Box
        ref={scrollContainerRef}
        data-testid="task-preview-scroll"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          // 체험 모드 토글로 컨텐츠 길이가 달라져도 스크롤바 자리는 항상 reserve
          // 해 좌우 여백이 흔들리지 않게 함.
          scrollbarGutter: "stable",
          ...taskEditorScrollbarSx,
          backgroundColor: theme.vars.palette.Background.Level1,
          borderRadius: "12px",
          pt: "12px",
          px: "12px",
          // 체험 모드 ON 에서는 마지막 블록도 중앙 스크롤 가능하도록, OFF 에서는
          // 시각적으로 ON 과 동일한 하단 여백을 유지하기 위해 같은 값 사용.
          // 컨테이너가 매우 큰 경우(데스크탑) 절반이 과해지는 걸 막아 240px cap.
          pb: `${Math.min((containerHeight || 144) / 2, 240)}px`,
        }}
      >
        {blocks.length === 0 ? (
          <Sheet
            sx={{
              border: "1px dashed",
              borderColor: theme.vars.palette.Border.Weak,
              backgroundColor: "transparent",
              borderRadius: "8px",
              p: "16px",
              textAlign: "center",
            }}
          >
            <Typography
              variant="Caption"
              sx={{ color: theme.vars.palette.Content.Weak }}
            >
              (블록 없음)
            </Typography>
          </Sheet>
        ) : (
          <Stack
            spacing={1}
            sx={{ maxWidth: "660px", mx: "auto", width: "100%" }}
          >
            {(() => {
              // 블록 1개 분량의 미리보기 아이템 (PreviewBlock + 진행 버튼).
              // 체험 모드의 노출/제외 스킵도 여기서 처리한다.
              const renderPreviewItem = (i: number) => {
                const block = blocks[i];
                if (!block) return null;
                const seq = i + 1;

                // 체험 모드: reveal 범위 바깥 + 분기 선택에서 제외된 블록 숨김.
                if (mode === "interactive") {
                  if (seq > session.state.revealedUpTo) return null;
                  if (session.excludedSequences.has(seq)) return null;
                }

                const isLastRevealed =
                  mode === "interactive" && seq === session.state.revealedUpTo;
                // header 블록은 단독으로 reveal 되지 않고 다음 body 와 묶여
                // 보이므로 끝에 "다음" 화살표가 붙지 않는다.
                const showGoNext =
                  isLastRevealed &&
                  seq < blocks.length &&
                  block.type !== "header" &&
                  block.action?.type !== "button_branch";
                return (
                  <Stack
                    key={i}
                    spacing={1}
                    ref={(el) => {
                      if (el) blockRefs.current.set(seq, el);
                      else blockRefs.current.delete(seq);
                    }}
                  >
                    <PreviewBlock
                      block={block}
                      blockSequence={seq}
                      displayLabel={displayLabels[i] ?? `블록 ${seq}`}
                      mode={mode}
                      session={session}
                      viewerMode={viewerMode}
                      totalBlocks={blocks.length}
                      onEdit={
                        onEditBlock
                          ? // raw index 전달 — 세그먼트 ✎ 의 드릴다운 진입
                            // 매핑은 부모(패널)가 한다.
                            () => onEditBlock(i)
                          : undefined
                      }
                    />
                    {showGoNext && (
                      <PreviewGoNextButton
                        disabled={!session.isBlockComplete(i)}
                        onClick={() => session.advanceFrom(i)}
                      />
                    )}
                  </Stack>
                );
              };

              // 체험 모드는 환자 시점 그대로 평탄하게 (스킵만), 정적 모드는
              // 분기 옵션을 ‹ › 페이저로 하나씩 보여준다 (빈 옵션 포함).
              if (mode === "interactive") {
                return blocks.map((_, i) => renderPreviewItem(i));
              }
              return blockGroups.map((group) => {
                const branchAction = blocks[group.index]?.action;
                if (
                  group.kind !== "branch" ||
                  branchAction?.type !== "button_branch" ||
                  branchAction.options.length === 0
                ) {
                  return renderPreviewItem(group.index);
                }
                return (
                  <Stack key={`branch-group-${group.index}`} spacing={1}>
                    {renderPreviewItem(group.index)}
                    <PreviewBranchPager
                      groupIndex={group.index}
                      optionCount={branchAction.options.length}
                      getOptionLabel={(optionIndex) => {
                        const optionText =
                          branchAction.options[optionIndex]?.text ?? "";
                        return `옵션 ${optionIndex + 1}${
                          optionText ? ` · ${optionText}` : ""
                        }`;
                      }}
                      renderOption={(optionIndex) => {
                        const segment =
                          group.segments.find(
                            (s) => s.optionIndex === optionIndex
                          ) ?? null;
                        if (!segment) {
                          return (
                            <Typography
                              sx={{
                                pl: "4px",
                                fontSize: "11px",
                                color: theme.vars.palette.Content.Weak,
                              }}
                            >
                              내부 블록 없음 — 선택 시 바로 합류
                            </Typography>
                          );
                        }
                        return (
                          <Stack spacing={1}>
                            {Array.from(
                              { length: segment.end - segment.start },
                              (_, offset) =>
                                renderPreviewItem(segment.start + offset)
                            )}
                          </Stack>
                        );
                      }}
                    />
                  </Stack>
                );
              });
            })()}
            {showFooter && <PreviewFooterBlocks />}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

export default memo(TaskEditorLivePreview);
